import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue } from "@utils/lodash";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import NavigationBar from "@components/NotificationBar/index";
import DeleteModal from "@_common/deleteModal";
import { toast } from "react-toastify";
import PaginationPage from "@components/pagination/pagination";
import SearchHeader from "@components/common/SearchHeader";
import Notfound from "@components/Not-Found";
import {
  clientGetVendor,
  clientUpdateBulkStatusVendor,
} from "@services/vendors.service";
import moment from "moment";
import {
  adminDeleteSpecificOrganization
} from "@services/client-list.service";
import { useParams } from "react-router-dom";
import Loader from "@components/common/Loader";
import { QueryRequestHelper } from "@_common/query-request-helper";
import ScrollableReactTable from "@components/ScrollableDatatable";
import MoreConfigSvgComponent from "../../../assets/svg-components/left-menu/more-config";
import RightMenuHeaders from "@components/common/RightMenuHeaders";

interface IVendorsDetailProps {}

const VendorsDetail: React.FunctionComponent<IVendorsDetailProps> = (
  props: any
) => {
  let navigate = useNavigate();
  let orgParams = useParams();
  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */

  const [isLoading, setIsLoading] = React.useState(false);
  const [list, setList] = React.useState<[]>([]);
  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0 && props.permissionAPITriggered) {
      getData();
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0 && props.permissionAPITriggered) {
      getData();
      if (params.s_search_text) {
        setSearchInput(params.s_search_text);
        setShowSearchTextbox(true);
      }
      if (params.s_page_no) {
        setPage_no(parseInt(params.s_page_no));
      }
      if (params.s_limit) {
        setLimit(parseInt(params.s_limit));
      }
    }
  }, [window.location.href, props.permissionAPITriggered]);

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      page_no: params.s_page_no ? parseInt(params.s_page_no) : 1,
      page_size: params.s_limit ? parseInt(params.s_limit) : s_limit,
      search_text: params.s_search_text,
      sort_by: params.sort_by,
      vendor_type: "SERVICING_VENDOR",
      parent_id: orgParams.id,
    };
    let queryRequest = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      let resp = await clientGetVendor(queryRequest);
      if (resp) {
        setIsLoading(false);
        setList(
          getValue(resp, `data.vendors`, []).map((item: object) => ({
            ...item,
            created_at: moment(getValue(item, `created_at`, "")).format(
              "DD MMM YYYY"
            ),
            status: getValue(item, `is_active`, false) ? "Active" : "Inactive",
            city_name: getValue(item, `city.name`, ""),
            industry: getValue(item, `industry.text`, ""),
            business_type: getValue(item, `business_type.text`, ""),
          }))
        );
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleChangeActiveStatus = async (is_active: string) => {
    try {
      let resp = await clientUpdateBulkStatusVendor({
        ids: selectedIDs,
        is_active: is_active === "active" ? true : false,
      });
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        setSelectedIDs([]);
        getData();
      }
    } catch (error) {}
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [s_search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.s_search_text) {
      delete params.s_search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    setSearchInput(e.target.value);

    let payload: any = {
      ...params,
      page_no:1,
      s_search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      list.length > 0
        ? list.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Delete section                             */
  /* -------------------------------------------------------------------------- */

  const [deleteValue, setDeleteValue] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const handleDeleteFun = (id: string, value: string) => {
    setDeleteValue(value);
    setDeleteId(id);
    handleDelete();
  };
  const [isDelete, setIsDelete] = useState(false);
  const handleDelete = () => {
    setIsDelete(!isDelete);
  };

  const handleDeleteFunction = async () => {
    try {
      let resp = await adminDeleteSpecificOrganization(deleteId);
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        handleDelete();
        getData();
      }
    } catch (error) {}
  };

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */

  const handleChangeSortBy = (value: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    params.sort_by = value;
    props.history.push({
      pathname: window.location.pathname,
      query: params,
    });
  };

  const handleChangeStatus = (value: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    params.status = value;
    props.history.push({
      pathname: window.location.pathname,
      query: params,
    });
  };
  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [s_totalCount, setTotalCount] = useState(0);
  const [s_limit, setLimit] = useState(10);
  const [s_page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/configuration/vendors/add/${id}`);
  };
  const rowClick = (obj: object) => {
    // console.log(getValue(obj,`original`,''))
    navigate(
      `/configuration/vendors/detail-info/profile/${orgParams.id}/${getValue(
        obj,
        `original.id`,
        ""
      )}?${mainQueryRequest}`
    );
  };
  const getTrProps = (rowInfo: any) => {
    if (getValue(rowInfo, `id`, "")) {
      return {
        style: {
          //   background:  'red',
          //   color: 'black'
        },
      };
    }
    return {};
  };
  const columns = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "vendor_code",
        width: 150,
        Cell: ({ row }: any) => {
          return (
            <NavLink
              to={`/configuration/vendors/detail-info/profile/${orgParams.id}/${row.original.id}`}
            >
              {row.values.vendor_code}
            </NavLink>
          );
        },
      },
      {
        Header: "Name",
        accessor: "vendor_name",
        width: 200,
      },
      {
        Header: "Industry",
        accessor: "industry",
        width: 150,
      },
      {
        Header: "Corporate Location",
        accessor: "city_name",
        width: 180,
      },
      {
        Header: "Business Type",
        accessor: "business_type",
        width: 180,
      },
      {
        Header: "Status",
        accessor: "status",
        width: 100,
      },
      // 	 {
      // Header: "Action",
      // width: 170,
      // Cell: ({ row }: any) => {
      // 	return (
      // 		<>
      // 			<Button
      // 				color="primary"
      // 				onClick={() => handleEdit(row.original.id)}
      // 			>
      // 				Edit
      // 			</Button>

      // 		</>
      // 	);
      // },
      //   },
    ],
    []
  );
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const [check, setCheck] = useState(false);
  const handleRemoveNavItems = () => {
    setCheck(!check);
    setSelectedIDs([]);
  };
  const urlParamsFun = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let request = QueryRequestHelper({ ...params });
    return request;
  };

  const mainQueryRequest = useMemo(() => urlParamsFun(), [
    window.location.href,
  ]);

  const urlParamsFunBack = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    delete params.s_page_no;
    delete params.s_limit;
    let request = QueryRequestHelper({ ...params });
    return request;
  };

  const mainQueryRequest_back = useMemo(() => urlParamsFunBack(), [
    window.location.href,
  ]);
  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      {getValue(selectedIDs, `length`, 0) > 0 && (
        <NavigationBar
          selectedIDs={selectedIDs}
          multi={
            getValue(props, `permissions`, []).includes("update") ? true : false
          }
          handleChangeActiveStatus={handleChangeActiveStatus}
          text={"MARK INACTIVE"}
          handleRemoveNavItems={handleRemoveNavItems}
        />
      )}

      {/* <div className="dashboard-wrapper__header">
        <h4 className="dashbaord-wrapper__header-title">
          Servicing Vendor / Sub Vendor
        </h4>
      </div> */}

      <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <NavLink
            to={`/configuration/vendors?${mainQueryRequest_back}`}
            className={`me-2`}
          >
            <img
              src="/images/back-link.svg"
              className="img-fluid ml-5"
              height={24}
              width={24}
            />
          </NavLink>
          <div className="header-parent-icon-holder">
            <MoreConfigSvgComponent />
          </div>

          <div className="title-wrapper">
            <h6 className="dashbaord-wrapper__header-parent-title">Vendor</h6>
            <h4 className="dashbaord-wrapper__header-title">
              Servicing Vendor / Sub Vendor
            </h4>
          </div>
        </div>

        <div className="d-flex">
          <RightMenuHeaders
            route={`/configuration/vendors/detail-info/profile/${orgParams.id}?${mainQueryRequest}`}
            //search
            isSearch={true}
            searchInput={s_search_text}
            showSearchTextbox={showSearchTextbox}
            toggleSearch={toggleSearch}
            toggleSearchClose={toggleSearchClose}
            handleChangeSearch={handleChangeSearch}
            add={
              getValue(props, `permissions`, []).includes("create")
                ? "true"
                : "false"
            }
            //Line Seperator of button
            lineSeperator={true}
          />
        </div>
      </div>

      <div className={`main-page-layout-wrapper`} id={getValue(selectedIDs, `length`, 0) > 0
					? "blueNavigationVisible"
					: ""}>
        <div className="admin-wrapper position-relative">
          {/* <div className="admin-wrapper position-relative">
            <SearchHeader
              route={`/configuration/vendors/detail-info/profile/${orgParams.id}?${mainQueryRequest}`}
              searchInput={s_search_text}
              showSearchTextbox={showSearchTextbox}
              toggleSearch={toggleSearch}
              toggleSearchClose={toggleSearchClose}
              handleChangeSearch={handleChangeSearch}
              isBulkUploadVisible={false}
              back={true}
              backUrl={`/configuration/vendors?${mainQueryRequest_back}`}
              add={
                getValue(props, `permissions`, []).includes("create")
                  ? "true"
                  : "false"
              }
            />
          </div> */}
          {isLoading ? (
            <Loader />
          ) : list.length > 0 ? (
            <div className="datatable__table">
              <ScrollableReactTable
                columns={columns}
                data={list}
                getTableState={getTableState}
                rowClick={rowClick}
                check={check}
                selectedIDs={selectedIDs}
                getTrProps={getTrProps}
              />
            </div>
          ) : (
            <>
              {Object.keys(params).length !== 0 ? (
                <>
                  <Notfound />
                </>
              ) : (
                <Notfound />
              )}
            </>
          )}
          {/* <div className="datatable__table">
            <DataTable
              columns={columns}
              data={list}
              getTableState={getTableState}
              rowClick={rowClick}
              check={check}
              selectedIDs={selectedIDs}
            />
          </div> */}
        </div>
        <div className="footer-wrapper d-flex justify-content-between">
          {list.length > 0 && (
            <img
              src="/images/building-illustration.svg"
              className="img-fluid"
            />
          )}
          {s_totalCount > 10 ? (
            <PaginationPage
              totalCount={s_totalCount}
              limit={s_limit}
              pageNumber={s_page_no}
              handleChangePagination={handleChangePagination}
              handleChangeLimit={handleChangeLimit}
            />
          ) : (
            <p className="show-rows-count mt-3 d-flex">
              Total Count:&nbsp;&nbsp;{" "}
              <p style={{ color: "rgb(13, 110, 253)" }}>{s_totalCount}</p>
            </p>
          )}
        </div>
        <DeleteModal
          isOpen={isDelete}
          handleModal={handleDelete}
          handleSubmit={handleDeleteFunction}
          deleteValue={deleteValue}
        />
      </div>
    </DashboardLayout>
  );
};

export default VendorsDetail;
